/** @jsxImportSource @emotion/react */
import { Button, VisuallyHidden } from "basis";
import { SvgInline } from "components/SvgInline/SvgInline";
import { IconName } from "components/SvgInline/types";
import { cardDetailsMap } from "components/WaysToPay/PaymentMethods/DebitCard/cardDetailsMap";
import { getIsPaymentInstrumentExpired } from "components/WaysToPay/PaymentMethods/DebitCard/getIsPaymentInstrumentExpired";
import { PaymentInstrument } from "library/api/payments";

interface PaymentInstrumentSelectionProps {
  instrument: PaymentInstrument;
  selected: boolean;
  onSelect: (instrumentId: string) => void;
  onRemove: (instrumentId: string) => void;
}

const PaymentInstrumentSelection = ({
  instrument,
  selected,
  onSelect,
  onRemove,
}: PaymentInstrumentSelectionProps) => {
  const cardDetails = cardDetailsMap[instrument.card_scheme];

  if (!cardDetails) {
    return null;
  }

  const {
    instrument_id: instrumentId,
    last_4_digits: last4Digits,
    expiry_date_month: expiryDateMonth,
    expiry_date_year: expiryDateYear,
  } = instrument;

  const id = `paymentInstrument-${instrumentId}`;

  const isExpired = getIsPaymentInstrumentExpired(instrument);

  const expiresText = `${
    isExpired ? "Expired" : "Expires"
  } ${expiryDateMonth}/${expiryDateYear}`;

  let background = "#f2f2f2";
  if (selected) {
    background = "#d0e9ff";
  }
  if (isExpired) {
    background = "rgba(242, 242, 242, .5)";
  }

  const { iconName, displayName } = cardDetails;
  const icon = isExpired ? `${iconName}-disabled` : iconName;

  const labelText = `${displayName} card with last 4 digits ${last4Digits}`;

  return (
    <div
      css={{
        background,
        display: "flex",
        marginBottom: "4px",
      }}
    >
      <label
        css={{
          appearance: "none",
          display: "grid",
          gridTemplateColumns: "auto 40px 1fr",
          padding: "15px",
          width: "100%",
          textAlign: "left",
          alignItems: "center",
          color: "initial",
        }}
        tabIndex={-1}
        htmlFor={id}
        key={instrumentId}
      >
        <input
          data-testid={instrumentId}
          css={{
            gridRow: "1/3",
            marginRight: "16px",
          }}
          type="radio"
          name="paymentInstrument"
          value={instrumentId}
          checked={selected}
          disabled={isExpired}
          id={id}
          onChange={() => onSelect(instrumentId)}
        />
        <div
          css={{
            gridRow: "1/3",
            lineHeight: 1,
          }}
        >
          <SvgInline name={icon as IconName} />
        </div>
        <div
          css={{
            marginLeft: "16px",
            color: isExpired ? "rgba(65, 65, 65, .6)" : "#006aff",
            fontWeight: 500,
          }}
          aria-hidden
        >
          {displayName} | •••• {last4Digits}
        </div>
        <VisuallyHidden>
          <span>{labelText}</span>
        </VisuallyHidden>
        <div
          css={{
            gridRow: 2,
            gridColumn: 3,
            marginLeft: "16px",
            fontSize: "14px",
            color: isExpired ? "#b3000c" : "",
          }}
          aria-hidden
        >
          {expiresText}
        </div>
      </label>
      <Button
        width="44"
        testId="delete-payment-instrument"
        variant="icon"
        onClick={() => onRemove(instrumentId)}
      >
        <SvgInline size="24px" name="delete" />
        <VisuallyHidden>
          <span>Delete</span>
        </VisuallyHidden>
      </Button>
    </div>
  );
};

interface SavedCardSelectionProps {
  paymentInstruments: PaymentInstrument[];
  selectedInstrumentId: string;
  onSelect: (instrumentId: string) => void;
  onRemove: (instrumentId: string) => void;
}

export const SavedCardSelection = ({
  paymentInstruments,
  onSelect,
  onRemove,
  selectedInstrumentId,
}: SavedCardSelectionProps) => (
  <div css={{ marginBottom: "20px" }}>
    {paymentInstruments.map((instrument) => (
      <PaymentInstrumentSelection
        key={instrument.instrument_id}
        instrument={instrument}
        selected={selectedInstrumentId === instrument.instrument_id}
        onSelect={onSelect}
        onRemove={onRemove}
      />
    ))}
  </div>
);
